export interface IManualRedactionBoundingBox {
  left: number
  top: number
  width: number
  height: number
}

export interface ITranslationPreviewLine {
  coordinateIndex: number | string
  originalText: string
  translatedText: string
  page: number
  boundingBox: IManualRedactionBoundingBox
}

export type TranslationSourceCoverTone = 'page' | 'primaryBand'

export interface INormalizedTranslationOverlayItem {
  line: ITranslationPreviewLine
  labelBoundingBox: IManualRedactionBoundingBox
  sourceCoverBoundingBox: IManualRedactionBoundingBox
  sourceCoverTone: TranslationSourceCoverTone
  horizontalPadding?: number
}

const translationSourceCoverHorizontalPadding = 0.02
const translationSourceCoverVerticalPadding = 0.008
const transactionHeaderBandTop = 0.285
const transactionHeaderBandBottom = 0.316
const icbcTopHeaderItems: Record<
  string,
  Pick<IManualRedactionBoundingBox, 'top' | 'height'>
> = {
  'Personal Account Statement': { top: 0.027, height: 0.022 },
}

const clampNormalizedCoordinate = (value: number) =>
  Math.max(0, Math.min(1, value))

const expandSourceCoverBoundingBox = (
  boundingBox: IManualRedactionBoundingBox,
) => {
  const left = clampNormalizedCoordinate(
    boundingBox.left - translationSourceCoverHorizontalPadding,
  )
  const top = clampNormalizedCoordinate(
    boundingBox.top - translationSourceCoverVerticalPadding,
  )
  const right = clampNormalizedCoordinate(
    boundingBox.left +
      boundingBox.width +
      translationSourceCoverHorizontalPadding,
  )
  const bottom = clampNormalizedCoordinate(
    boundingBox.top +
      boundingBox.height +
      translationSourceCoverVerticalPadding,
  )

  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  }
}

const isIcbcChineseStatement = (lines: ITranslationPreviewLine[]) =>
  lines.some(
    (line) =>
      line.originalText.includes('中国工商银行') ||
      line.translatedText.includes('Industrial and Commercial Bank of China'),
  )

const isIcbcTransactionHeader = (line: ITranslationPreviewLine) => {
  const bottom = line.boundingBox.top + line.boundingBox.height

  return (
    line.boundingBox.top > 0.26 && bottom < transactionHeaderBandBottom + 0.02
  )
}

const splitMultilineTranslationLine = (
  line: ITranslationPreviewLine,
): ITranslationPreviewLine[] => {
  const translatedParts = line.translatedText
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (translatedParts.length <= 1) {
    return [line]
  }

  const rowHeight = line.boundingBox.height / translatedParts.length

  return translatedParts.map((translatedText, index) => ({
    ...line,
    coordinateIndex: `${line.coordinateIndex}-${index}`,
    translatedText,
    boundingBox: {
      ...line.boundingBox,
      top: line.boundingBox.top + rowHeight * index,
      height: rowHeight,
    },
  }))
}

const splitIcbcAdjacentColumnHeader = (
  line: ITranslationPreviewLine,
): ITranslationPreviewLine[] => {
  const translatedText = line.translatedText.trim()
  if (
    !translatedText.includes('Credit Amount') ||
    !translatedText.includes('Account Balance')
  ) {
    return [line]
  }

  const firstColumnWidthRatio = 0.46
  const firstColumnWidth = line.boundingBox.width * firstColumnWidthRatio
  const secondColumnLeft = line.boundingBox.left + firstColumnWidth

  return [
    {
      ...line,
      coordinateIndex: `${line.coordinateIndex}-1`,
      translatedText: 'Credit Amount',
      boundingBox: {
        ...line.boundingBox,
        width: firstColumnWidth,
      },
    },
    {
      ...line,
      coordinateIndex: `${line.coordinateIndex}-2`,
      translatedText: 'Account Balance',
      boundingBox: {
        ...line.boundingBox,
        left: secondColumnLeft,
        width: line.boundingBox.width - firstColumnWidth,
      },
    },
  ]
}

const isIcbcSplitAdjacentColumnHeader = (line: ITranslationPreviewLine) =>
  ['Credit Amount', 'Account Balance'].includes(line.translatedText.trim())

const resolveIcbcSourceCoverBoundingBox = (line: ITranslationPreviewLine) => {
  if (!isIcbcTransactionHeader(line)) {
    return expandSourceCoverBoundingBox(line.boundingBox)
  }

  return {
    left: 0.064,
    top: transactionHeaderBandTop,
    width: 0.87,
    height: transactionHeaderBandBottom - transactionHeaderBandTop,
  }
}

const resolveIcbcLabelBoundingBox = (
  line: ITranslationPreviewLine,
): IManualRedactionBoundingBox => {
  const text = line.translatedText.trim()

  const topHeaderItem = icbcTopHeaderItems[text]
  if (topHeaderItem) {
    return { ...line.boundingBox, ...topHeaderItem }
  }

  // Prefix-match variable header content so any statement number or print date matches.
  if (text.startsWith('Statement No:')) {
    return { ...line.boundingBox, top: 0.048, height: 0.014 }
  }
  if (text.startsWith('Print Date:')) {
    return { ...line.boundingBox, top: 0.063, height: 0.014 }
  }

  if (!isIcbcTransactionHeader(line)) {
    return line.boundingBox
  }

  return {
    ...line.boundingBox,
    top: 0.294,
    height: 0.011,
  }
}

const resolveIcbcSourceCoverTone = (
  line: ITranslationPreviewLine,
): TranslationSourceCoverTone => {
  const isTopHeaderBand = line.boundingBox.top < 0.11

  return isTopHeaderBand || isIcbcTransactionHeader(line)
    ? 'primaryBand'
    : 'page'
}

const normalizeGenericStatementLines = (lines: ITranslationPreviewLine[]) =>
  lines.flatMap(splitMultilineTranslationLine).map((line) => ({
    line,
    labelBoundingBox: line.boundingBox,
    sourceCoverBoundingBox: expandSourceCoverBoundingBox(line.boundingBox),
    sourceCoverTone: 'page' as TranslationSourceCoverTone,
  }))

const normalizeIcbcChineseStatementLines = (lines: ITranslationPreviewLine[]) =>
  lines
    .flatMap(splitMultilineTranslationLine)
    .flatMap(splitIcbcAdjacentColumnHeader)
    .map((line) => ({
      line,
      labelBoundingBox: resolveIcbcLabelBoundingBox(line),
      sourceCoverBoundingBox: resolveIcbcSourceCoverBoundingBox(line),
      sourceCoverTone: resolveIcbcSourceCoverTone(line),
      ...(isIcbcSplitAdjacentColumnHeader(line)
        ? { horizontalPadding: 0.008 }
        : {}),
    }))

export const normalizeTranslationOverlayItems = (
  lines: ITranslationPreviewLine[],
): INormalizedTranslationOverlayItem[] => {
  if (isIcbcChineseStatement(lines)) {
    return normalizeIcbcChineseStatementLines(lines)
  }

  return normalizeGenericStatementLines(lines)
}
